import { useState, useEffect } from "react";
import { Filter, SortAsc } from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { API } from "../App";

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sizeFilter, setSizeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("default");

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let url = `${API}/products`;
        if (sizeFilter !== "all") {
          url += `?size=${sizeFilter}`;
        }
        const response = await axios.get(url);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [sizeFilter]);

  const sortedProducts = [...products].sort((a, b) => {
    switch (sortBy) {
      case "price-low":
        return a.price - b.price;
      case "price-high":
        return b.price - a.price;
      case "weight":
        return a.weight - b.weight;
      default:
        return 0;
    }
  });

  const sizes = [
    { value: "all", label: "All Sizes" },
    { value: "250g", label: "250g" },
    { value: "500g", label: "500g" },
    { value: "1000g", label: "1kg" },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="products-page">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Our Products</h1>
            <p className="text-muted-foreground max-w-2xl">
              Premium Guntur mirchi powder in various pack sizes. Choose the one that suits your needs best.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-8 p-4 bg-card/50 rounded-xl border border-white/5" data-testid="filters-section">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Filter:</span>
            </div>
            
            {/* Size Filter Pills */}
            <div className="flex flex-wrap gap-2">
              {sizes.map((size) => (
                <button
                  key={size.value}
                  onClick={() => setSizeFilter(size.value)}
                  className={`size-pill ${sizeFilter === size.value ? 'selected' : ''}`}
                  data-testid={`filter-${size.value}`}
                >
                  {size.label}
                </button>
              ))}
            </div>

            <div className="flex-1"></div>

            {/* Sort */}
            <div className="flex items-center gap-2">
              <SortAsc className="w-4 h-4 text-muted-foreground" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40 bg-background border-white/10" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                  <SelectItem value="weight">Weight</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-card rounded-xl aspect-square animate-pulse"></div>
              ))}
            </div>
          ) : sortedProducts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">No products found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
              {sortedProducts.map((product, index) => (
                <ProductCard key={product.product_id} product={product} index={index} />
              ))}
            </div>
          )}

          {/* Info Cards */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-card border border-white/5 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-foreground mb-2">Bulk Orders</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Need larger quantities? Contact us for wholesale pricing and custom pack sizes.
              </p>
              <Button variant="outline" className="rounded-full border-white/20">
                Contact for Bulk Orders
              </Button>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-foreground mb-2">First Order Discount</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Use code <span className="text-secondary font-mono font-semibold">WELCOME10</span> at checkout for 10% off your first order.
              </p>
              <p className="text-xs text-muted-foreground">*Minimum order value ₹200</p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductsPage;
